import { inject } from "@angular/core";
import { HttpErrorResponse, HttpHandlerFn, HttpInterceptorFn, HttpRequest, HttpStatusCode } from "@angular/common/http";
import { Router } from "@angular/router";
import { catchError, concatMap, delay, EMPTY, finalize, Observable, switchMap, takeUntil, tap, throwError, timer } from "rxjs";
import { DialogCustomService } from "@shared/layout/services/dialog-custom.service";
import { BaseUtilities } from "@shared/library/configs/helpers/base-utilities";
import { AuthService } from "@core/server/auth.service";
import { CookieService } from "ngx-cookie-service";

export const ErrorResponseInterceptor: HttpInterceptorFn = (
  req: HttpRequest<unknown>,
  next: HttpHandlerFn
): Observable<any> => {
  const _routerSvc: Router = inject( Router );

  const dialogSvc: DialogCustomService = inject( DialogCustomService );
  const authSvc: AuthService = inject( AuthService );
  const cookieSvc: CookieService = inject( CookieService );

  return next(req).pipe(
    catchError((error: HttpErrorResponse) => handleErrorResponse( error, req, next, _routerSvc, dialogSvc, authSvc, cookieSvc ))
  );
};

function handleErrorResponse(
  error: HttpErrorResponse,
  req: HttpRequest<unknown>,
  next: HttpHandlerFn,
  _routerSvc: Router,
  dialogSvc: DialogCustomService,
  authSvc: AuthService,
  cookieSvc: CookieService
): Observable<any> {
  if( error.status === HttpStatusCode.Unauthorized ) {
    if( !authSvc.isRefreshing() ) {
      authSvc.setRefreshing( true );
      const refreshTimeout$ = timer( authSvc.timerInactivityErrorRefreshingMS );

      return authSvc.refresh().pipe(
        finalize(() => authSvc.setRefreshing( false )),
        concatMap((response) => {
          authSvc.setUser( response );

          const currentUrl = _routerSvc.url;
          if (currentUrl.includes('/home/operaciones-alta')) {
            // window.location.reload();
            return EMPTY;
          }

          const clonedRequest = authSvc.addToken( req );
          return next( clonedRequest );
        }),
        catchError(() => {
          return showDialogError( error, _routerSvc, dialogSvc, undefined, undefined, true ).pipe(
            tap(() => {
              authSvc.setRefreshing( false );
              authSvc.logOut();
            }),
            takeUntil(refreshTimeout$),
            finalize(() => { if( cookieSvc.get( 'user' ) || authSvc.isRefreshing() ) authSvc.logOut() }),
            switchMap(() => EMPTY)
          );
        })
      );
    } else {
      return authSvc.refresh().pipe(
        concatMap((response) => {
          authSvc.setUser( response );

          const currentUrl = _routerSvc.url;
          if (currentUrl.includes('/home/operaciones-alta')) {
            window.location.reload();
            return EMPTY;
          }

          const clonedRequest = authSvc.addToken( req );
          return next( clonedRequest );
        }),
        catchError(() => {
          const errorResponse: string = `Error ${error.status}. ${error.message}`;
          return throwError(() => errorResponse);
        })
      );
    }
  }

  if( !authSvc.isRefreshing() ) showDialogError( error, _routerSvc, dialogSvc );
  
  const errorResponse: string = `Error ${error.status}. ${error.message}`;
  return throwError(() => errorResponse);
}

function showDialogError(
  error: HttpErrorResponse,
  _routerSvc: Router,
  dialogSvc: DialogCustomService,
  fnCancel?: Function, 
  fnOk?: Function,
  isObservable: boolean = false
): any | Observable<any> {

  const dialogError = dialogSvc.showErrorDialog(
    'error',
    BaseUtilities.getInfoErrorDialog( error ),
    fnCancel,
    fnOk
  );

  console.error( error );
  return isObservable ? dialogError.afterClosed() : undefined;
}